import { useEffect, useState } from "react";

interface ShareDialogProps {
  open: boolean;
  onClose: () => void;
  movieTitle: string;
}

export default function ShareDialog({ open, onClose, movieTitle }: ShareDialogProps) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) setCopied(false);
  }, [open]);

  if (!open) return null;

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareTitle = `${movieTitle} - 秋霞电影`;

  const handleCopy = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        const input = document.createElement("textarea");
        input.value = shareUrl;
        input.style.position = "fixed";
        input.style.opacity = "0";
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
      }
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const socials = [
    {
      label: "微博",
      icon: "ri-weibo-fill",
      href: `https://service.weibo.com/share/share.php?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareTitle)}`,
    },
    {
      label: "QQ 空间",
      icon: "ri-qq-fill",
      href: `https://connect.qq.com/widget/shareqq/index.html?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareTitle)}`,
    },
    {
      label: "豆瓣",
      icon: "ri-douban-fill",
      href: `https://www.douban.com/share/service?href=${encodeURIComponent(shareUrl)}&name=${encodeURIComponent(shareTitle)}`,
    },
  ];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-primary-950/55 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative w-full max-w-md rounded-lg bg-background-50 border border-background-200 p-6 animate-fade-up">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-heading text-lg text-foreground-950">分享这部电影</h3>
            <p className="mt-1 text-xs text-foreground-500">把《{movieTitle}》分享给同样爱看电影的人</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="关闭分享"
            className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="mt-5 flex items-center gap-2">
          <div className="flex-1 min-w-0 h-10 px-3 rounded-md border border-background-200 bg-background-100 flex items-center overflow-hidden">
            <span className="text-xs text-foreground-600 truncate">{shareUrl}</span>
          </div>
          <button
            type="button"
            onClick={handleCopy}
            className="h-10 px-4 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            {copied ? "已复制" : "复制链接"}
          </button>
        </div>

        <div className="mt-6">
          <p className="text-xs text-foreground-500 mb-3">分享到社交平台</p>
          <div className="grid grid-cols-3 gap-2">
            {socials.map((social) => (
              <a
                key={social.label}
                href={social.href}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="h-20 rounded-md border border-background-200 bg-background-50 flex flex-col items-center justify-center gap-2 text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer"
              >
                <i className={`${social.icon} text-xl`}></i>
                <span className="text-xs">{social.label}</span>
              </a>
            ))}
          </div>
        </div>

        <p className="mt-5 text-[11px] leading-relaxed text-foreground-400">
          提示：演示站点分享链接为当前页面地址，可直接复制到任意设备打开。
        </p>
      </div>
    </div>
  );
}